import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import moment from "moment";
import type { FilterShipmentsDescription } from "@/Shared/ShipmentFilter/Dialogs/Internal/FilterShipmentsDialogBody";

/**
 * Whether a shipment survives the filters the customer has applied.
 *
 * Every field is optional and every one that is set has to match — the filters
 * narrow, they never widen. Most are plain equality; the date ranges are
 * exclusive at both ends, and the two delivered-at ranges also require the
 * shipment to actually be delivered, since an undelivered shipment has no
 * delivery date to compare against.
 */
// oxlint-disable-next-line complexity, sonarjs/cognitive-complexity -- grandfathered, see ENG-7672
export function viewShipmentsMatchesFilter(
  shipment: Shipment,
  filter: FilterShipmentsDescription,
): boolean {
  if (filter.proNumber !== undefined) {
    if (filter.proNumber !== shipment.proNumber) {
      return false;
    }
  }

  if (filter.bolNumber !== undefined) {
    if (filter.bolNumber !== shipment.billOfLadingNumber) {
      return false;
    }
  }

  if (filter.carrierIdentifier !== undefined) {
    if (
      filter.carrierIdentifier !== shipment.selectedQuote?.carrierIdentifier
    ) {
      return false;
    }
  }

  if (filter.minPriceDollars !== undefined) {
    if (shipment.selectedQuote?.price === undefined) {
      return false;
    }

    if (filter.minPriceDollars >= shipment.selectedQuote?.price) {
      return false;
    }
  }

  if (filter.maxPriceDollars !== undefined) {
    if (shipment.selectedQuote?.price === undefined) {
      return false;
    }

    if (filter.maxPriceDollars <= shipment.selectedQuote?.price) {
      return false;
    }
  }

  if (filter.shipmentState !== undefined && filter.shipmentState.length > 0) {
    if (filter.shipmentState !== shipment.shipmentState) {
      return false;
    }
  }

  if (filter.pickupReferenceNumber !== undefined) {
    if (filter.pickupReferenceNumber !== shipment.pickupReferenceNumber) {
      return false;
    }
  }

  if (filter.pickupBusinessName !== undefined) {
    if (filter.pickupBusinessName !== shipment.pickupLocation.businessName) {
      return false;
    }
  }

  if (filter.pickupPostalCode !== undefined) {
    if (
      filter.pickupPostalCode !== shipment.pickupLocation.address.postalCode
    ) {
      return false;
    }
  }
  if (filter.pickupStateOrProvinceCode !== undefined) {
    if (
      filter.pickupStateOrProvinceCode !==
      shipment.pickupLocation.address.stateOrProvinceCode
    ) {
      return false;
    }
  }

  if (filter.deliveryReferenceNumber !== undefined) {
    if (filter.deliveryReferenceNumber !== shipment.deliveryReferenceNumber) {
      return false;
    }
  }

  if (filter.deliveryBusinessName !== undefined) {
    if (
      filter.deliveryBusinessName !== shipment.deliveryLocation.businessName
    ) {
      return false;
    }
  }

  if (filter.deliveryPostalCode !== undefined) {
    if (
      filter.deliveryPostalCode !== shipment.deliveryLocation.address.postalCode
    ) {
      return false;
    }
  }
  if (filter.deliveryStateOrProvinceCode !== undefined) {
    if (
      filter.deliveryStateOrProvinceCode !==
      shipment.deliveryLocation.address.stateOrProvinceCode
    ) {
      return false;
    }
  }

  if (filter.bookedAtStartRange !== undefined) {
    if (
      moment(filter.bookedAtStartRange).isSameOrAfter(moment(shipment.bookedAt))
    ) {
      return false;
    }
  }

  if (filter.bookedAtEndRange !== undefined) {
    if (
      moment(filter.bookedAtEndRange).isSameOrBefore(moment(shipment.bookedAt))
    ) {
      return false;
    }
  }

  if (filter.pickupDateStartRange !== undefined) {
    if (
      moment(filter.pickupDateStartRange).isSameOrAfter(
        moment(shipment.pickupDate),
      )
    ) {
      return false;
    }
  }

  if (filter.pickupDateEndRange !== undefined) {
    if (
      moment(filter.pickupDateEndRange).isSameOrBefore(
        moment(shipment.pickupDate),
      )
    ) {
      return false;
    }
  }

  if (filter.deliveredAtStartRange !== undefined) {
    if (shipment.shipmentState !== ShipmentState.Delivered) {
      return false;
    }

    if (
      moment(filter.deliveredAtStartRange).isSameOrAfter(
        moment(shipment.actualDeliveryDate),
      )
    ) {
      return false;
    }
  }

  if (filter.deliveredAtEndRange !== undefined) {
    if (shipment.shipmentState !== ShipmentState.Delivered) {
      return false;
    }

    if (
      moment(filter.deliveredAtEndRange).isSameOrBefore(
        moment(shipment.actualDeliveryDate),
      )
    ) {
      return false;
    }
  }

  if (filter.onlyShowFreightClaims !== undefined) {
    return shipment.hasFreightClaims === true;
  }

  return true;
}
